import { Fragment } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconChevronLeft } from "@tabler/icons-react";

/**
 * How Back shows in the breadcrumb. `separate`: a chevron of its own, for a
 * Back that may go somewhere the trail does not name (the previous page, a
 * cold entry's fallback). `first-crumb`: Back returns to the first crumb, so
 * the chevron and that crumb are one control, with no dead gap between two
 * targets that lead to the same place. `none`: a page the sidebar opens, with
 * nothing to go back to.
 */
export type BreadcrumbBack =
  | { kind: "none" }
  | { kind: "separate"; onBack: () => void }
  | { kind: "first-crumb"; onBack: () => void };

/**
 * Where a desktop page sits, in its top bar (Figma ".DesktopTopNav /
 * Breadcrumb"): Back, the parent pages as links in slate/600, and the current
 * page closing the trail in Medium slate/700, cut at 480px.
 */
export function DesktopBreadcrumb({
  back,
  trail,
  current,
}: {
  back: BreadcrumbBack;
  /**
   * A crumb's `to` may carry router state: a crumb back to a collection opened
   * from a congress space restores what that page reads to name the space.
   */
  trail: { label: string; to: LinkProps["to"] }[];
  current: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [first, ...rest] = trail;
  if (back.kind === "first-crumb" && first === undefined) {
    throw new Error(
      "should not happen: Back joins a first crumb that is absent"
    );
  }
  const crumbs = back.kind === "first-crumb" ? rest : trail;
  return (
    <nav
      aria-label={t("Breadcrumb")}
      className="flex min-w-0 items-center gap-1.5 font-dm-sans text-ui-body"
    >
      {back.kind === "separate" ? (
        <button
          type="button"
          onClick={back.onBack}
          aria-label={t("Back")}
          className="-ml-1 flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 text-ds-slate-600 hover:bg-ds-slate-100"
        >
          <IconChevronLeft className="size-4" stroke={1.5} />
        </button>
      ) : null}
      {back.kind === "first-crumb" && first !== undefined ? (
        <>
          <button
            type="button"
            onClick={back.onBack}
            className="-ml-1 flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border-none bg-transparent px-1 py-0 font-dm-sans text-ui-body text-ds-slate-600 hover:text-ds-slate-800"
          >
            <IconChevronLeft className="size-4" stroke={1.5} />
            {first.label}
          </button>
          <span aria-hidden className="text-ds-slate-600">
            /
          </span>
        </>
      ) : null}
      {crumbs.map((crumb) => (
        <Fragment key={crumb.label}>
          <Link
            to={crumb.to}
            className="shrink-0 text-ds-slate-600 no-underline hover:text-ds-slate-800"
          >
            {crumb.label}
          </Link>
          <span aria-hidden className="text-ds-slate-600">
            /
          </span>
        </Fragment>
      ))}
      <span
        aria-current="page"
        className="max-w-120 truncate font-medium text-ds-slate-700"
      >
        {current}
      </span>
    </nav>
  );
}
